import React from 'react';
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface ActionButtonsProps {
  existData: any;
  fieldNo: string;
  item: any;
  fieldActions: { [key: number]: {
    verified: boolean;
    updated: boolean;
    comment: string;
    updateData: any;
    submitted?: boolean;
  } };
  editingIndex: number | null;
  setEditingIndex: (index: number | null) => void;
  onVerifiedChange: (fieldIndex: number) => void;
  onCommentChange: (fieldIndex: number, comment: string) => void;
  onUpdateSubmit: (index: number, responses: any[]) => void;
  updatedFields: any; // Add this prop
  annexureType?: string;
  userDetail:any;
  isValidationActive?: boolean;
  hasValidationError?: boolean;
  fieldIndex?: number;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ 
  existData,
  fieldNo, 
  item, 
  fieldActions,
  editingIndex,
  setEditingIndex,
  onVerifiedChange,
  onCommentChange,
  onUpdateSubmit,
  updatedFields, // Add this prop
  annexureType = "annexureOne",
  userDetail,
  isValidationActive = false,
  hasValidationError = false,
  fieldIndex,
}) => {
  const index = parseInt(fieldNo);
  const fieldAction = fieldActions[index] || { verified: false, updated: false, comment: "", updateData: {} };

    const roleIdentifier = userDetail.Role.Role_Identifier;

  // Get the correct annexure data based on the type
const isFieldVerifiedInExistData = () => {
  const annexureData = getAnnexureData();
  let isVerifiedForRole = false;

  item.response.forEach((r) => {
    const reSubmittedFieldsData = existData?.apmcSchemeTransaction?.rejectedFields?.rejectedFieldsData?.[annexureType === "annexureTwo" ? "annexureTwo" : annexureType === "annexureThree" ? "annexureThree" : "annexureOne"];
    const transactions = annexureData?.[r.fieldName]?.transaction || [];
    let lastRoleTx = null;
 const shouldCheckResubmission = existData?.appStatusLevel !== 'pendingAtHigherAuthorityApproval';
    if (shouldCheckResubmission && reSubmittedFieldsData && Object.prototype.hasOwnProperty.call(reSubmittedFieldsData, r.fieldName)) {
      // If resubmitted field, only set lastRoleTx if the last transaction is for this role
      if (transactions.length > 0) {
        const lastTx = transactions[transactions.length - 1];
        if (lastTx.transactionBy === roleIdentifier) {
          lastRoleTx = lastTx;
        }
      }
    } else {
      // Not a resubmitted field: filter by role as before
      lastRoleTx = transactions.filter(
        (tx) => tx.transactionBy === roleIdentifier
      ).pop();
    }

    if (lastRoleTx && lastRoleTx.isVerified === true) {
      isVerifiedForRole = true;
    }
  });

  return isVerifiedForRole;
};

const getAnnexureData = () => {
  switch(annexureType) {
    case "annexureTwo":
      return existData?.annexureTwo;
    case "annexureThree":
      return existData?.annexureThree;
    default:
      return existData?.annexureOne;
  }
};

  // Check if there are actual updates in updatedFields for this field
  const hasActualUpdates = () => {
    const fieldName = item.response.find((r) => r?.fieldName)?.fieldName;
    if (!fieldName) return false;
    
    // Check if this field exists in updatedFields and has actual data
    return updatedFields && updatedFields[fieldName] && 
           (updatedFields[fieldName].docId || 
            Object.keys(updatedFields[fieldName]).some(key => 
              updatedFields[fieldName][key] !== null && 
              updatedFields[fieldName][key] !== undefined && 
              updatedFields[fieldName][key] !== ""
            ));
  };

  // Check if field was submitted with updates (from existData transaction history)
  const isFieldUpdatedInExistData = () => {
    const fieldName = item.response.find((r) => r?.fieldName)?.fieldName;
    const annexureData = getAnnexureData();
    
    if (!fieldName || !annexureData?.[fieldName]) return false;
    
    const annexureField = annexureData[fieldName];
    return annexureField?.transaction?.some(
      (txn) => (txn?.isUpdated === true && txn?.transactionBy == roleIdentifier && txn.appStatusLevel==existData.appStatusLevel)
    ) ?? false;
  };

  
  

  const handleVerifiedChange = () => {
    onVerifiedChange(index);
  };

  const handleCommentChange = (comment: string) => {
    onCommentChange(index, comment);
  };

  const handleUpdateSubmit = () => {
    onUpdateSubmit(index, item.response);
  };

  const handleEditClick = () => {
    setEditingIndex(editingIndex === index ? null : index);
  };

const isVerifySubmittedInExistData = isFieldVerifiedInExistData();
const isVerifyPending = fieldAction.verified && !fieldAction.submitted && !isVerifySubmittedInExistData;
const isVerifySubmitted = (fieldAction.verified && fieldAction.submitted) || isVerifySubmittedInExistData;

  // Determine if the field should show as "Updated"
const shouldShowUpdated =  isFieldUpdatedInExistData()

return (
  <div 
    id={`actionButtons_${fieldIndex}`}
    className={`d-flex flex-column w-100 px-4 justify-content-center gap-2 align-items-center ${
      isValidationActive && hasValidationError ? 'border border-danger bg-danger-subtle rounded p-2' : ''
    }`}
  >
    {/* Validation error message */}
    {isValidationActive && hasValidationError && (
      <div className="alert alert-danger p-2 w-100 text-center mb-2">
        <small className="d-flex align-items-center justify-content-center">
          <i className="feather feather-alert-triangle me-1"></i>
          Action required before proceeding
        </small>
      </div>
    )}

    <div className='w-100 d-flex flex-column gap-2'>
      <div className="d-flex gap-2 flex-column flex-xl-row">
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id={`tooltip-verify-${fieldNo}`}>
              {isVerifySubmitted ? 'Verified' : 'Click to Verify'}
            </Tooltip>
          }
        >
          <button
            type="button"
            className={`btn fs-15 ${
              isVerifySubmitted 
                ? 'btn-success' 
                : isVerifyPending 
                  ? 'btn-light-green' 
                  : 'btn-outline-primary'
            }`}
            style={{ 
              minWidth: '80px',
              width:"100%",
              backgroundColor: isVerifyPending ? '#d4edda' : undefined,
              borderColor: isVerifyPending ? '#c3e6cb' : undefined,
              color: isVerifyPending ? '#155724' : undefined
            }}
            onClick={handleVerifiedChange}
          >
            {isVerifySubmitted ? "Verified" : "Verify"}
          </button>
        </OverlayTrigger>

    {(item?.fieldNo!=="03."&&item?.fieldNo!=="11.") &&  <OverlayTrigger
          placement="bottom"
          overlay={<Tooltip id="tooltip-bottom">Edit</Tooltip>}
        >
          <button
            type="button"
            className={`btn ${shouldShowUpdated ? 'btn-info' : 'btn-outline-info'}`}
            style={{ minWidth: '80px', width:'100%' }}
            onClick={handleEditClick}
          >
            {shouldShowUpdated ? "Updated" : "Update"}
          </button>
        </OverlayTrigger>}
      </div>

      <div className='d-flex align-items-center justify-content-center border border-secondary rounded-1'>
        <textarea
          className="form-control form-control-sm border-0"
          placeholder="Add comment"
          value={fieldAction.comment}
          onChange={(e) => handleCommentChange(e.target.value)}
        />
      </div>
  
      <button
        className={`btn w-100 ${
          (!fieldAction.verified && 
          !isFieldVerifiedInExistData() &&
          !hasActualUpdates() && 
          !fieldAction.comment.trim()) 
            ? 'btn-secondary' 
            : 'btn-outline-success'
        }`}
        onClick={handleUpdateSubmit}
        disabled={
          !fieldAction.verified && 
          !isFieldVerifiedInExistData() &&
          !hasActualUpdates() && 
          !fieldAction.comment.trim()
        }
      >
        Submit
      </button>
      
      {/* Enhanced instruction text */}
      <p className='small text-success mb-0'>
        Please Select Update/Verify then click Submit
        {isValidationActive && hasValidationError && (
          <span className="text-danger d-block mt-1">
            <i className="feather feather-arrow-up me-1"></i>This field requires action
          </span>
        )}
      </p>
    </div>
  </div>
);
};

export default ActionButtons;